import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Facebook, Mail, Phone } from "lucide-react";
import { useEnquiry } from "./EnquiryContext";
import { ComplaintsModal } from "./ComplaintsModal";

export const Footer = () => {
  const { openEnquiryModal } = useEnquiry();
  const currentYear = new Date().getFullYear();
  const [complaintsModalOpen, setComplaintsModalOpen] = useState(false);

  const handleContactClick = () => {
    openEnquiryModal("general");
  };

  return (
    <footer className="bg-gradient-to-b from-teal-800 to-teal-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo and About */}
          <div className="md:col-span-1">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-3 mb-4 cursor-pointer"
              data-testid="footer-logo-btn"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_github-pull-8/artifacts/1ub08pix_ChatGPT%20Image%20Apr%2028%2C%202026%2C%2002_38_32%20PM.png" 
                alt="Cozee Care" 
                className="h-20 w-20 object-contain bg-white rounded-lg p-1"
              />
              <div className="text-left">
                <div className="font-bold text-lg">Barnston Court</div>
                <div className="text-sm text-teal-200">Nursing Home</div>
              </div>
            </button>
            <p className="text-teal-100 text-sm leading-relaxed">
              Compassionate care in the heart of Moreton, proudly operated by Cozee Care.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2 text-teal-100">
              <li>
                <a href="#about" className="hover:text-orange-300 transition-colors text-sm">
                  About Us
                </a>
              </li>
              <li>
                <a href="#principles" className="hover:text-orange-300 transition-colors text-sm">
                  Our Principles
                </a>
              </li>
              <li>
                <a href="#care-types" className="hover:text-orange-300 transition-colors text-sm">
                  Care Services
                </a>
              </li>
              <li>
                <a href="#facilities" className="hover:text-orange-300 transition-colors text-sm">
                  Facilities
                </a>
              </li>
              <li>
                <a 
                  href="https://www.cqc.org.uk/location/1-111147708" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-orange-300 transition-colors text-sm"
                >
                  CQC Report
                </a>
              </li>
              <li>
                <button 
                  onClick={handleContactClick}
                  className="hover:text-orange-300 transition-colors text-sm"
                  data-testid="footer-contact-us-btn"
                >
                  Contact Us
                </button>
              </li>
              <li>
                <a 
                  href="/admin" 
                  className="hover:text-orange-300 transition-colors text-sm"
                  data-testid="footer-admin-link"
                >
                  Admin Centre
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-lg mb-4">Our Services</h3>
            <ul className="space-y-2 text-teal-100">
              <li className="text-sm">Residential Care</li>
              <li className="text-sm">Nursing Care</li>
              <li className="text-sm">Dementia Care</li>
              <li className="text-sm">End-of-Life Care</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4">Contact Us</h3>
            <div className="space-y-3 text-teal-100">
              <div className="flex items-start gap-2">
                <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <a href="tel:01516775573" className="hover:text-orange-300 transition-colors text-sm">
                    0151 677 5573
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <a href="mailto:manager@barnstoncourt.com" className="hover:text-orange-300 transition-colors text-sm break-all">
                    manager@barnstoncourt.com
                  </a>
                </div>
              </div>
              <div className="text-sm">
                <p>21 Barnston Lane</p>
                <p>Moreton, Wirral</p>
                <p>CH46 7TN</p>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-teal-700 my-8"></div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-teal-200 text-sm text-center md:text-left">
            <p>© {currentYear} Barnston Court Nursing Home. Operated by Cozee Care Homes Ltd.</p>
            <p className="mt-1">CQC Location ID: 1-111147708 &middot; ICO Registration: ZA470038</p>
          </div>

          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-4 text-sm text-teal-200">
            <Link
              to="/privacy-policy"
              className="hover:text-orange-300 transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span>|</span>
            <Link
              to="/cookie-policy"
              className="hover:text-orange-300 transition-colors"
              data-testid="footer-cookie-link"
            >
              Cookie Policy
            </Link>
            <span>|</span>
            <button 
              onClick={() => alert("Safeguarding: This would link to your safeguarding policy. Please add the actual safeguarding content.")}
              className="hover:text-orange-300 transition-colors"
            >
              Safeguarding
            </button>
            <span>|</span>
            <button 
              onClick={() => setComplaintsModalOpen(true)}
              className="hover:text-orange-300 transition-colors"
              data-testid="footer-complaints-btn"
            >
              Complaints
            </button>
          </div>

          {/* Social Media - Facebook only */}
          <div className="flex gap-3">
            <a
              href="https://www.facebook.com/BarnstonCourt"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-teal-700 hover:bg-orange-500 p-2 rounded-full transition-colors"
              aria-label="Facebook"
              data-testid="footer-facebook-link"
            >
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Made with Love */}
        <div className="text-center mt-8 text-teal-300 text-sm flex items-center justify-center gap-2">
          <span>Caring for our community with</span>
          <Heart className="h-4 w-4 text-orange-400 fill-orange-400" />
        </div>
      </div>

      {/* Complaints Modal */}
      <ComplaintsModal 
        isOpen={complaintsModalOpen} 
        onClose={() => setComplaintsModalOpen(false)} 
      />
    </footer>
  );
};
